// Matchlight Studio: where the club keeps its crest, squads, player photos and sponsors, and looks back over its season.
// /studio.html?venue=wensleydale — protected by the club's admin code.
import { mountHelp } from './help';
import { loadClub, esc, param, byNumber, type Club, type Player, venueParam, brandHeader } from './net';
import { nameFromFile, sponsorNameFromFile } from '../shared/imaging';
import { shortName, LIMITS, DEFAULT_TAGLINE, type Credit } from '../shared/club';
import { SPORTS, getRules } from '../shared/rules';
import { seasonTotals, seasonCsv, type MatchRecord } from '../shared/season';
import { preparePhoto, prepareCrest, prepareLogo, prepareLeagueCrest, uploadImage, uploadVideo, videoDuration } from './imaging-browser';

const venue = venueParam();
const $ = <T extends HTMLElement = HTMLElement>(s: string, root: ParentNode = document) => root.querySelector(s) as T;
const app = $('#app'), bar = $('#savebar'), toastEl = $('#toast');
if (param('venue')) document.querySelectorAll<HTMLAnchorElement>('header nav a').forEach(a => { const u = new URL(a.href); u.searchParams.set('venue', venue); a.href = u.href; });
let code = '';
try { code = localStorage.getItem(`ml-admin-${venue}`) ?? ''; } catch { /* storage blocked */ }

let club!: Club;
let loaded = false;
let squadKey = '';
let dirty = false;
let busy = '';
let credit: Credit | null = null;   // the Matchlight credit: only fetched (and shown) for the owner's code
let season: MatchRecord[] = [];      // the season record, newest first; saved by the venue at full time, not with the club
let seasonPick = '', seasonTeam = '', seasonOpen = '';

const rid = () => Math.random().toString(36).slice(2, 8);
let toastTimer = 0;
function toast(msg: string, bad = false) {
  toastEl.textContent = msg; toastEl.className = bad ? 'bad' : ''; toastEl.hidden = false;
  clearTimeout(toastTimer); toastTimer = window.setTimeout(() => (toastEl.hidden = true), bad ? 6000 : 2500);
}
function markDirty() { dirty = true; drawBar(); }
function drawBar() {
  bar.innerHTML = busy ? `<span class="busy">${esc(busy)}</span>`
    : dirty ? `<span>You have changes that aren't on the screen yet.</span><button class="go" id="save">Save changes</button>`
    : `<span class="ok">All changes saved. The screen and phones use them straight away.</span>`;
  bar.hidden = false;
  $('#save', bar)?.addEventListener('click', save);
}
addEventListener('beforeunload', e => { if (dirty) { e.preventDefault(); } });

// ---- sign in ----
function signIn(msg = '') {
  bar.hidden = true;
  app.innerHTML = `<section class="card narrow"><h2>Studio</h2><p>Enter the club's admin code to edit teams and sponsors.</p>
    <form id="login"><label class="field">Admin code<input id="code" type="password" autocomplete="current-password" required></label>
    ${msg ? `<p class="err">${esc(msg)}</p>` : ''}<button class="go">Continue</button></form></section>`;
  ($('#login') as HTMLFormElement).onsubmit = async e => { e.preventDefault(); code = ($('#code') as HTMLInputElement).value.trim(); await start(); };
}
async function start() {
  // check the code with a harmless admin call before showing the editor
  const r = await fetch(`/api/venue/${venue}/admin`, { headers: { 'x-admin-code': encodeURIComponent(code) } });
  if (r.status === 401) return signIn(code ? 'That admin code is not right.' : '');
  try { localStorage.setItem(`ml-admin-${venue}`, code); } catch { /* fine */ }
  if (!loaded || !dirty) { club = await loadClub(venue); squadKey = Object.keys(club.squads)[0] ?? ''; loaded = true; }   // keep unsaved edits after a re-sign-in
  // the owner's code also gets the Matchlight credit; a club's own code is refused and sees nothing of it
  const cr = await fetch(`/api/venue/${venue}/credit`, { headers: { 'x-admin-code': encodeURIComponent(code) } }).catch(() => null);
  credit = cr?.ok ? await cr.json() : null;
  await loadSeason();
  render(); drawBar();
}

// ---- page ----
function render() {
  const sq = club.squads[squadKey];
  app.innerHTML = `
  <section class="card" id="clubCard"><h2>Club</h2>
    <div class="clubgrid">
      <div class="crestbox"><div class="crestprev">${club.crest ? `<img src="${esc(club.crest)}" alt="Club crest">` : '<span>No crest yet</span>'}</div>
        <label class="filebtn">Replace crest<input type="file" id="crestFile" accept="image/*"></label>
        <label class="check"><input type="checkbox" id="crestCut" checked> Remove white around the crest</label></div>
      <div class="fields">
        <label class="field">Club name<input data-club="name" value="${esc(club.name)}" maxlength="40"></label>
        <label class="field">Name on the scoreboard<input data-club="teamName" value="${esc(club.teamName)}" maxlength="24"></label>
        <label class="field">Three-letter short name<input data-club="short" value="${esc(club.short)}" maxlength="3"></label>
        <label class="field">Club colour<input type="color" data-club="color" value="${esc(club.color)}"></label>
        <label class="field">Sport<select data-club="sport">${SPORTS.map(x => `<option value="${x.id}" ${(club.sport ?? 'rugby-union') === x.id ? 'selected' : ''}>${esc(x.name)}</option>`).join('')}</select>
          <span class="note">Sets the scorer's buttons and the clock. Applies from the next match started.</span></label>
      </div>
    </div>
  </section>

  <section class="card" id="squadCard"><h2>Teams and players</h2>
    <div class="tabs" role="tablist">${Object.entries(club.squads).map(([k, s]) =>
      `<button role="tab" aria-selected="${k === squadKey}" class="${k === squadKey ? 'on' : ''}" data-squad="${esc(k)}">${esc(s.label)} <small>${s.players.length}</small></button>`).join('')}
      <form class="addsquad" id="addSquad"><input id="newSquad" placeholder="New team, e.g. Under 16s" maxlength="30" aria-label="New team name"><button>Add a team</button></form></div>
    ${sq ? `
    <div class="squadhead">
      <label class="field">Team name<input id="squadLabel" value="${esc(sq.label)}" maxlength="30"></label>
      ${Object.keys(club.squads).length > 1 ? '<button class="quiet" id="removeSquad">Remove this team</button>' : ''}
    </div>
    <div class="drop" id="photoDrop">
      <label class="filebtn big">Add player photos<input type="file" id="photoFiles" accept="image/*" multiple></label>
      <p>or drop them here. Name each file <b>firstname_lastname.jpg</b> and the names fill in for you. Put the shirt number first (<b>12_sarah_jones.jpg</b>) to fill that too.</p>
      <div class="opts"><label class="check"><input type="checkbox" id="optCut" checked> Cut out the white background</label>
        <label class="check"><input type="checkbox" id="optBadge" checked> Remove the club badge from the bottom-left corner</label></div>
    </div>
    <div class="players">${sq.players.map(p => playerCard(p)).join('')}</div>
    <button class="quiet" id="addPlayer">Add a player without a photo</button>
    ${sq.players.length ? `<button class="quiet" id="sortPlayers">Sort by shirt number</button>` : ''}` : '<p>No teams yet. Add one above.</p>'}
  </section>

  ${seasonCard()}

  <section class="card" id="sponsorCard"><h2>Sponsors</h2>
    <p class="hint">Logos rotate along the bottom of the scoreboard and full screen on the Sponsors view, in this order.</p>
    <div class="drop"><label class="filebtn big">Add sponsor logos<input type="file" id="logoFiles" accept="image/*" multiple></label>
      <p>Square logos work best. The background colour is taken from the logo's corner; change it if it looks wrong.</p></div>
    <div class="sponsors">${club.sponsors.map((s, i) => `
      <div class="sponsor" data-i="${i}">
        <div class="logo" style="background:${esc(s.bg)}"><img src="${esc(s.logo)}" alt=""></div>
        <label class="field">Name<input data-sp="name" value="${esc(s.name)}" maxlength="40"></label>
        <label class="field">Background<input type="color" data-sp="bg" value="${esc(s.bg)}"></label>
        <div class="moves"><button class="small" data-move="-1" ${i === 0 ? 'disabled' : ''} aria-label="Move up">↑</button>
          <button class="small" data-move="1" ${i === club.sponsors.length - 1 ? 'disabled' : ''} aria-label="Move down">↓</button>
          <button class="small quiet" data-delsp aria-label="Remove ${esc(s.name)}">Remove</button></div>
      </div>`).join('') || '<p class="hint">No sponsors yet.</p>'}</div>
  </section>

  <section class="card" id="leagueCard"><h2>League opponents</h2>
    <p class="hint">The clubs in your league. Matchday setup offers them in its opponent list; cup opponents can still be typed in there.</p>
    <div class="drop"><label class="filebtn big">Add club crests<input type="file" id="leagueFiles" accept="image/*" multiple></label>
      <p>Name each file after the club (<b>Doncaster.png</b>, <b>West_Leeds.jpg</b>). The three-letter name and team colour are worked out from the crest; change them if they look wrong.</p>
      <label class="check"><input type="checkbox" id="leagueCut" checked> Remove white around each crest</label></div>
    <div class="league">${(club.league ?? []).map((o, i) => `
      <div class="opp" data-i="${i}">
        <div class="ocrest">${o.crest ? `<img src="${esc(o.crest)}" alt="">` : '<span>No crest</span>'}<input type="file" accept="image/*" data-recrest title="Replace crest"></div>
        <label class="field">Club<input data-op="name" value="${esc(o.name)}" maxlength="24"></label>
        <div class="orow"><label class="field">Short<input data-op="short" value="${esc(o.short)}" maxlength="3"></label>
          <label class="field">Colour<input type="color" data-op="color" value="${esc(o.color)}"></label>
          <button class="small quiet" data-delop aria-label="Remove ${esc(o.name)}">Remove</button></div>
      </div>`).join('') || '<p class="hint">No league opponents yet.</p>'}</div>
    ${(club.league ?? []).length > 1 ? '<button class="quiet" id="sortLeague">Sort A–Z</button>' : ''}
  </section>

  <section class="card" id="videoCard"><h2>Promo videos</h2>
    <p class="hint">Bar offers, sponsor adverts or club news. The scorer can play one from the phone, usually at half-time; the score stays in the corner while it plays.</p>
    <div class="drop"><label class="filebtn big">Add a video<input type="file" id="videoFiles" accept="video/mp4,video/webm" multiple></label>
      <p>MP4 or WebM, up to ${LIMITS.videoBytes / 1_000_000} MB each. Make it 720 × 480 (3:2) to fill the screen; other shapes get black bars. Most 30-second clips exported for social media fit easily.</p></div>
    <div class="videos">${(club.videos ?? []).map((v, i) => `
      <div class="vid" data-i="${i}">
        <video src="${esc(v.url)}" muted preload="metadata" controls playsinline></video>
        <label class="field">Title<input data-vd="title" value="${esc(v.title)}" maxlength="60"></label>
        <p class="hint">${v.durationMs ? fmtDur(v.durationMs) : 'Length unknown'} · ${(v.bytes / 1_000_000).toFixed(1)} MB</p>
        <button class="small quiet" data-delvd aria-label="Remove ${esc(v.title)}">Remove</button>
      </div>`).join('') || '<p class="hint">No videos yet.</p>'}</div>
  </section>${credit ? creditCard(credit) : ''}`;
  wire();
  wireSeason();
  wireLeague();
  wireVideos();
}

// ---- season record ----
async function loadSeason() {
  const r = await fetch(`/api/venue/${venue}/season`, { headers: { 'x-admin-code': encodeURIComponent(code) } }).catch(() => null);
  season = r?.ok ? ((await r.json()) as { matches: MatchRecord[] }).matches : [];
  const seasons = [...new Set(season.map(m => m.season))];
  if (!seasons.includes(seasonPick)) seasonPick = seasons[0] ?? '';
}
const fmtDate = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/London' });
const plural = (w: string, n: number) => n === 1 ? w : w.endsWith('y') ? `${w.slice(0, -1)}ies` : `${w}s`;   // try → tries, penalty → penalties
const RESULT = { won: 'W', drawn: 'D', lost: 'L' } as const;
/** "Smith 2, Jones" or, for football, "Smith 23', 67'" */
const scorerText = (m: MatchRecord, side: 'home' | 'away') => m[side].scorers
  .map(x => getRules(m.sport).scorerMinutes ? `${x.name} ${x.mins.join(', ')}` : `${x.name}${x.n > 1 ? ` ${x.n}` : ''}`).join(' · ');

function seasonMatches() {
  return season.filter(m => m.season === seasonPick && (!seasonTeam || (m.squad ?? '') === seasonTeam));
}

function seasonCard() {
  const seasons = [...new Set(season.map(m => m.season))];
  const intro = `<p class="hint">Every match on the screen is saved here at the final whistle: the score, who scored, cards and today's team. Away matches aren't on the screen, so they aren't in the record.</p>`;
  if (!season.length) return `<section class="card" id="seasonCard"><h2>Season record</h2>${intro}<p class="hint">No matches yet. Your first one appears here at full time.</p></section>`;
  const inSeason = season.filter(m => m.season === seasonPick);
  const teams = [...new Map(inSeason.map(m => [m.squad ?? '', m.squadLabel ?? (m.squad ? club.squads[m.squad]?.label : '') ?? ''])).entries()];
  if (seasonTeam && !teams.some(([k]) => k === seasonTeam)) seasonTeam = '';
  const list = seasonMatches(), t = seasonTotals(list);
  const words = getRules(list[0]?.sport ?? club.sport ?? 'rugby-union').words;
  const Scores = words.scores[0].toUpperCase() + words.scores.slice(1);
  const hasLeague = list.some(m => m.league);
  return `
  <section class="card" id="seasonCard"><h2>Season record</h2>
    ${intro}
    <div class="seasonpick">
      <label class="field">Season<select id="seasonPick">${seasons.map(x => `<option ${x === seasonPick ? 'selected' : ''}>${esc(x)}</option>`).join('')}</select></label>
      ${teams.length > 1 ? `<label class="field">Team<select id="seasonTeam"><option value="">All teams</option>${teams.map(([k, l]) => `<option value="${esc(k)}" ${k === seasonTeam ? 'selected' : ''}>${esc(l || 'No team picked')}</option>`).join('')}</select></label>` : ''}
      <button class="quiet" id="seasonCsv">Download as a spreadsheet</button>
    </div>
    <div class="stats">
      <div><b>${t.played}</b>Played</div><div><b>${t.won}</b>Won</div><div><b>${t.drawn}</b>Drawn</div><div><b>${t.lost}</b>Lost</div>
      <div><b>${t.pointsFor}</b>${words.score === 'goal' ? 'Goals for' : 'Points for'}</div><div><b>${t.pointsAgainst}</b>${words.score === 'goal' ? 'Goals against' : 'Points against'}</div>
      ${hasLeague ? `<div><b>${t.leaguePoints}</b>League points${t.bonus ? `<small>${t.bonus} bonus</small>` : ''}</div>` : ''}
    </div>
    ${t.scorers.length ? `<div class="topscorers"><h3>Top ${esc(words.score)} scorers</h3><ol>${t.scorers.slice(0, 10).map(x => `<li><span>${esc(x.name)}</span><b>${x.n}</b></li>`).join('')}</ol></div>` : ''}
    <div class="results">${list.map(m => `
      <details class="result" data-id="${esc(m.id)}" ${m.id === seasonOpen ? 'open' : ''}>
        <summary>
          <span class="when">${esc(fmtDate(m.date))}</span>
          <span class="wl ${m.result}">${RESULT[m.result]}</span>
          <span class="vs">v ${esc(m.away.name)}${!seasonTeam && m.squadLabel && teams.length > 1 ? ` <small>${esc(m.squadLabel)}</small>` : ''}</span>
          <span class="sc">${m.home.points}–${m.away.points}</span>
          <span class="comp">${m.league ? `League${m.leaguePoints != null ? ` · ${m.leaguePoints} pt${m.leaguePoints === 1 ? '' : 's'}` : ''}` : 'Cup or friendly'}${m.finished ? '' : ' · ended early'}</span>
        </summary>
        <div class="detail">
          ${(['home', 'away'] as const).map(k => `<div class="side"><h4>${esc(m[k].name)} ${m[k].points}</h4>
            <p>${m[k].counts.map(c => `${c.n} ${esc(plural(c.label.toLowerCase(), c.n))}`).join(', ') || 'No score'}${m[k].bonus.length ? ` · bonus point${m[k].bonus.length > 1 ? 's' : ''}: ${m[k].bonus.map(b => b === 'try' ? words.score : 'losing').join(', ')}` : ''}</p>
            ${m[k].scorers.length ? `<p><span class="hint">${esc(Scores)}:</span> ${esc(scorerText(m, k))}</p>` : ''}
            ${m[k].cards.length ? `<p><span class="hint">Cards:</span> ${m[k].cards.map(c => esc(`${c.label}${c.who ? ` ${c.who}` : ''} ${c.min}`)).join(' · ')}</p>` : ''}
            ${m[k].team.length ? `<p class="teamsheet"><span class="hint">Team:</span> ${m[k].team.map(p => esc(`${p.number ? `${p.number} ` : ''}${p.name}`)).join(', ')}</p>` : ''}
          </div>`).join('')}
          ${m.timeline.length ? `<ol class="timeline">${m.timeline.map(e => `<li><b>${esc(e.min)}</b> ${esc(e.label)} · ${esc(m[e.team].name)}${e.who ? ` · ${esc(e.who)}` : ''}</li>`).join('')}</ol>` : ''}
          <button class="small quiet" data-delmatch>Remove from the record</button>
        </div>
      </details>`).join('') || '<p class="hint">No matches for this team this season.</p>'}</div>
  </section>`;
}

function wireSeason() {
  $('#seasonPick')?.addEventListener('change', e => { seasonPick = (e.target as HTMLSelectElement).value; render(); });
  $('#seasonTeam')?.addEventListener('change', e => { seasonTeam = (e.target as HTMLSelectElement).value; render(); });
  $('#seasonCsv')?.addEventListener('click', () => {
    const list = seasonMatches(), words = getRules(list[0]?.sport ?? club.sport ?? 'rugby-union').words;
    const blob = new Blob([seasonCsv([...list].reverse(), words.scores[0].toUpperCase() + words.scores.slice(1))], { type: 'text/csv' });
    const a = document.createElement('a');
    const team = seasonTeam ? season.find(m => m.squad === seasonTeam)?.squadLabel ?? '' : '';
    a.href = URL.createObjectURL(blob);
    a.download = `${club.name} ${team ? `${team} ` : ''}${seasonPick} results.csv`.replace(/[\\/:*?"<>|]/g, '');
    a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  });
  app.querySelectorAll<HTMLDetailsElement>('.result').forEach(d => {
    d.addEventListener('toggle', () => { if (d.open) seasonOpen = d.dataset.id!; else if (seasonOpen === d.dataset.id) seasonOpen = ''; });
    d.querySelector<HTMLButtonElement>('[data-delmatch]')!.addEventListener('click', e => {
      const b = e.currentTarget as HTMLButtonElement;
      // asks twice: a removed match can't be brought back
      if (b.dataset.sure !== '1') { b.dataset.sure = '1'; b.textContent = 'Remove this match for good? Click again'; return; }
      work('Removing the match…', async () => {
        const r = await fetch(`/api/venue/${venue}/season?id=${encodeURIComponent(d.dataset.id!)}`, { method: 'DELETE', headers: { 'x-admin-code': encodeURIComponent(code) } });
        if (!r.ok) throw new Error('Could not remove that match. Try again.');
        season = season.filter(m => m.id !== d.dataset.id); seasonOpen = '';
        await loadSeason(); render(); toast('Removed from the season record.');
      });
    });
  });
}

const fmtDur = (ms: number) => { const s = Math.round(ms / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

function wireLeague() {
  const league = (club.league ??= []);
  $('#leagueFiles')?.addEventListener('change', e => {
    const files = [...((e.target as HTMLInputElement).files ?? [])];
    work('', async step => {
      let n = 0;
      const failed: string[] = [];
      for (const f of files) {
        step(`Preparing crest ${++n} of ${files.length}: ${f.name}`);
        try {
          const { blob, color } = await prepareLeagueCrest(f, ($('#leagueCut') as HTMLInputElement).checked);
          const crest = await uploadImage(venue, code, blob);
          const name = sponsorNameFromFile(f.name).slice(0, 24);
          const same = league.find(o => o.name.toLowerCase() === name.toLowerCase());
          if (same) { same.crest = crest; } else league.push({ name, short: shortName(name), color, crest });
        } catch (err) { failed.push((err as Error).message); }
      }
      markDirty(); render();
      if (failed.length) toast(failed.join(' '), true); else toast(`${n} crest${n === 1 ? '' : 's'} added. Check the names, then save.`);
    });
  });
  $('#sortLeague')?.addEventListener('click', () => { league.sort((a, b) => a.name.localeCompare(b.name)); markDirty(); render(); });
  app.querySelectorAll<HTMLElement>('.opp').forEach(row => {
    const o = league[Number(row.dataset.i)];
    row.querySelectorAll<HTMLInputElement>('[data-op]').forEach(inp => inp.addEventListener('input', () => {
      if (inp.dataset.op === 'short') { inp.value = inp.value.replace(/[^A-Za-z]/g, '').toUpperCase(); o.short = inp.value; }
      else (o as any)[inp.dataset.op!] = inp.value;
      markDirty();
    }));
    row.querySelector('[data-delop]')!.addEventListener('click', () => { league.splice(league.indexOf(o), 1); markDirty(); render(); });
    row.querySelector<HTMLInputElement>('[data-recrest]')!.addEventListener('change', e => {
      const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
      work(`Preparing the crest for ${o.name}…`, async () => {
        const { blob, color } = await prepareLeagueCrest(f, ($('#leagueCut') as HTMLInputElement).checked);
        o.crest = await uploadImage(venue, code, blob); o.color = color; markDirty(); render();
      });
    });
  });
}

function wireVideos() {
  const videos = (club.videos ??= []);
  $('#videoFiles')?.addEventListener('change', e => {
    const files = [...((e.target as HTMLInputElement).files ?? [])];
    work('', async step => {
      let n = 0;
      for (const f of files) {
        if (f.size > LIMITS.videoBytes) { toast(`${f.name} is ${(f.size / 1_000_000).toFixed(0)} MB. The limit is ${LIMITS.videoBytes / 1_000_000} MB, so shorten it or export it smaller.`, true); continue; }
        step(`Uploading video ${++n} of ${files.length}: ${f.name} (${(f.size / 1_000_000).toFixed(1)} MB)…`);
        try {
          const durationMs = await videoDuration(f);
          const { url, bytes } = await uploadVideo(venue, code, f);
          videos.push({ id: `v${rid()}`, title: sponsorNameFromFile(f.name), url, durationMs, bytes });
        } catch (err) { toast((err as Error).message, true); }
      }
      markDirty(); render();
    });
  });
  app.querySelectorAll<HTMLElement>('.vid').forEach(row => {
    const v = videos[Number(row.dataset.i)];
    row.querySelector<HTMLInputElement>('[data-vd="title"]')!.addEventListener('input', e => { v.title = (e.target as HTMLInputElement).value; markDirty(); });
    row.querySelector('[data-delvd]')!.addEventListener('click', () => { videos.splice(videos.indexOf(v), 1); markDirty(); render(); });
  });
}

function playerCard(p: Player) {
  return `<div class="player" data-id="${esc(p.id)}">
    <label class="face" title="Replace photo">${p.photo ? `<img src="${esc(p.photo)}" alt="">` : '<span>Add photo</span>'}<input type="file" accept="image/*" data-replace></label>
    <div class="pfields">
      <label class="num">No.<input data-p="number" inputmode="numeric" maxlength="3" value="${esc(p.number)}"></label>
      <label class="nm">Name<input data-p="name" maxlength="40" value="${esc(p.name)}" placeholder="Full name"></label>
    </div>
    <div class="prow"><label class="check small"><input type="checkbox" data-p="showPhoto" ${p.showPhoto !== false ? 'checked' : ''}> Photo on screen</label>
      <button class="small quiet" data-delp aria-label="Remove ${esc(p.name || 'player')}">Remove</button></div>
  </div>`;
}

function wire() {
  // club fields
  app.querySelectorAll<HTMLInputElement>('[data-club]').forEach(i => i.addEventListener('input', () => {
    (club as any)[i.dataset.club!] = i.dataset.club === 'short' ? i.value.toUpperCase() : i.value; markDirty();
  }));
  $('#crestFile')?.addEventListener('change', async e => {
    const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
    await work('Preparing the crest…', async () => {
      club.crest = await uploadImage(venue, code, await prepareCrest(f, ($('#crestCut') as HTMLInputElement).checked));
      markDirty(); render();
    });
  });

  // squads
  app.querySelectorAll<HTMLButtonElement>('[data-squad]').forEach(b => b.onclick = () => { squadKey = b.dataset.squad!; render(); });
  ($('#addSquad') as HTMLFormElement).onsubmit = e => {
    e.preventDefault();
    const label = ($('#newSquad') as HTMLInputElement).value.trim(); if (!label) return;
    squadKey = `${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'squad'}-${rid()}`;
    club.squads[squadKey] = { label, players: [] }; markDirty(); render();
  };
  const sq = club.squads[squadKey];
  if (!sq) { wireSponsors(); wireCredit(); return; }
  $('#squadLabel').addEventListener('input', e => { sq.label = (e.target as HTMLInputElement).value; markDirty(); });
  $('#removeSquad')?.addEventListener('click', e => {
    const b = e.currentTarget as HTMLButtonElement;
    // a team with players asks twice, so nobody loses a squad's photos with one click
    if (sq.players.length && b.dataset.sure !== '1') { b.dataset.sure = '1'; b.textContent = `Remove ${sq.label} and its ${sq.players.length} player${sq.players.length > 1 ? 's' : ''}? Click again`; return; }
    delete club.squads[squadKey]; squadKey = Object.keys(club.squads)[0] ?? ''; markDirty(); render();
  });
  $('#addPlayer').onclick = () => { sq.players.push({ id: `p${rid()}`, name: '', number: '', photo: '', showPhoto: true }); markDirty(); render(); };
  $('#sortPlayers')?.addEventListener('click', () => { sq.players = byNumber(sq.players); markDirty(); render(); });
  const addPhotos = (files: File[]) => work('', async step => {
    const opts = { cutout: ($('#optCut') as HTMLInputElement).checked, clearBadge: ($('#optBadge') as HTMLInputElement).checked };
    let done = 0, failed: string[] = [];
    for (const f of files.filter(f => f.type.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(f.name))) {
      step(`Preparing photo ${++done} of ${files.length}: ${f.name}`);
      try {
        const url = await uploadImage(venue, code, await preparePhoto(f, opts));
        const { name, number } = nameFromFile(f.name);
        const same = name && sq.players.find(p => p.name.toLowerCase() === name.toLowerCase());
        if (same) { same.photo = url; if (number) same.number = number; }     // a new photo for someone already listed
        else sq.players.push({ id: `p${rid()}`, name, number, photo: url, showPhoto: true });
      } catch (err) { failed.push((err as Error).message); }
    }
    markDirty(); render();
    if (failed.length) toast(failed.join(' '), true); else toast(`${done} photo${done === 1 ? '' : 's'} added. Check the names, then save.`);
  });
  $('#photoFiles').addEventListener('change', e => addPhotos([...((e.target as HTMLInputElement).files ?? [])]));
  const drop = $('#photoDrop');
  drop.addEventListener('dragover', e => { e.preventDefault(); drop.classList.add('over'); });
  drop.addEventListener('dragleave', () => drop.classList.remove('over'));
  drop.addEventListener('drop', e => { e.preventDefault(); drop.classList.remove('over'); addPhotos([...(e.dataTransfer?.files ?? [])]); });

  app.querySelectorAll<HTMLElement>('.player').forEach(card => {
    const p = sq.players.find(x => x.id === card.dataset.id)!;
    card.querySelectorAll<HTMLInputElement>('[data-p]').forEach(i => i.addEventListener('input', () => {
      if (i.dataset.p === 'showPhoto') p.showPhoto = i.checked;
      else if (i.dataset.p === 'number') { i.value = i.value.replace(/[^0-9]/g, ''); p.number = i.value; }
      else p.name = i.value;
      markDirty();
    }));
    card.querySelector('[data-delp]')!.addEventListener('click', () => { sq.players = sq.players.filter(x => x !== p); markDirty(); render(); });
    card.querySelector<HTMLInputElement>('[data-replace]')!.addEventListener('change', e => {
      const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
      work(`Preparing the photo for ${p.name || 'this player'}…`, async () => {
        p.photo = await uploadImage(venue, code, await preparePhoto(f, { cutout: ($('#optCut') as HTMLInputElement).checked, clearBadge: ($('#optBadge') as HTMLInputElement).checked }));
        markDirty(); render();
      });
    });
  });
  wireSponsors();
  wireCredit();
}

/** Owner only (Tidemark code): Matchlight's own slot in the sponsor rotation. Saved by itself, not with the club. */
function creditCard(c: Credit) {
  return `
  <section class="card" id="creditCard"><h2>Matchlight credit <small>(only you can see this)</small></h2>
    <p class="hint">"Scoreboard by Matchlight" takes one sponsor slot in six, and fills the strip when the club shows no sponsors. Club admins don't see this section.</p>
    <label class="check"><input type="checkbox" id="creditOn" ${c.on ? 'checked' : ''}> Show the Matchlight credit on this club's screen</label>
    <label class="field">Tagline<input id="creditTag" value="${esc(c.tagline)}" maxlength="60" placeholder="${esc(DEFAULT_TAGLINE)}"></label>
    <button class="go" id="creditSave">Save the credit</button>
  </section>`;
}

function wireCredit() {
  $('#creditSave')?.addEventListener('click', () => work('Saving the credit…', async () => {
    const body = { on: ($('#creditOn') as HTMLInputElement).checked, tagline: ($('#creditTag') as HTMLInputElement).value };
    const r = await fetch(`/api/venue/${venue}/credit`, { method: 'PUT', body: JSON.stringify(body), headers: { 'content-type': 'application/json', 'x-admin-code': encodeURIComponent(code) } });
    if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error ?? 'Could not save the credit');
    credit = await r.json(); render(); toast(credit!.on ? 'Saved. The Matchlight credit is on the screen.' : 'Saved. The Matchlight credit is off for this club.');
  }));
}

function wireSponsors() {
  $('#logoFiles')?.addEventListener('change', e => {
    const files = [...((e.target as HTMLInputElement).files ?? [])];
    work('', async step => {
      let n = 0;
      for (const f of files) {
        step(`Preparing logo ${++n} of ${files.length}: ${f.name}`);
        try { const { blob, bg } = await prepareLogo(f); club.sponsors.push({ name: sponsorNameFromFile(f.name), logo: await uploadImage(venue, code, blob), bg }); }
        catch (err) { toast((err as Error).message, true); }
      }
      markDirty(); render();
    });
  });
  app.querySelectorAll<HTMLElement>('.sponsor').forEach(row => {
    const i = Number(row.dataset.i), s = club.sponsors[i];
    row.querySelectorAll<HTMLInputElement>('[data-sp]').forEach(inp => inp.addEventListener('input', () => {
      (s as any)[inp.dataset.sp!] = inp.value;
      if (inp.dataset.sp === 'bg') (row.querySelector('.logo') as HTMLElement).style.background = inp.value;
      markDirty();
    }));
    row.querySelectorAll<HTMLButtonElement>('[data-move]').forEach(b => b.onclick = () => {
      const j = i + Number(b.dataset.move);
      [club.sponsors[i], club.sponsors[j]] = [club.sponsors[j], club.sponsors[i]]; markDirty(); render();
    });
    row.querySelector('[data-delsp]')!.addEventListener('click', () => { club.sponsors.splice(i, 1); markDirty(); render(); });
  });
}

async function work(label: string, fn: (step: (s: string) => void) => Promise<void>) {
  const step = (s: string) => { busy = s; drawBar(); };
  step(label || 'Working…');
  try { await fn(step); } catch (e) { toast((e as Error).message, true); }
  finally { busy = ''; drawBar(); }
}

async function save() {
  await work('Saving…', async () => {
    const r = await fetch(`/api/venue/${venue}/club`, { method: 'PUT', body: JSON.stringify(club), headers: { 'content-type': 'application/json', 'x-admin-code': encodeURIComponent(code) } });
    const out = await r.json().catch(() => ({}));
    if (r.status === 401) { signIn('Your admin code has changed. Sign in again; your edits are kept until you leave this page.'); return; }
    if (!r.ok) throw new Error(out.error ?? 'Could not save');
    club = out; dirty = false; brandHeader(out);
    if (!club.squads[squadKey]) squadKey = Object.keys(club.squads)[0] ?? '';
    render(); toast('Saved. The screen has the new details.');
  });
}

mountHelp({ page: 'studio', venue, code: () => code, context: () => document.getElementById('login') ? 'They have not signed in with the admin code yet.' : '' });

loadClub(venue).then(c => { brandHeader(c); return code ? start() : signIn(); })
  .catch(() => { app.innerHTML = `<section class="card"><h2>No club called “${esc(venue)}”</h2></section>`; });
